import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { isEmpty, isUndefined } from "lodash";
import React from "react";
import { CustomFindingColumn } from "types/tenantConfig";
import { resolveCustomColumnValues } from "utils/customColumnUtils";
import { CellRenderers } from "utils/tableUtils";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";

export default function useCustomColumns<
  T extends { tags?: string[] | string; tagList?: string[] | string },
>({
  externalColumn,
  keyName = "tags",
}: {
  externalColumn: CustomFindingColumn[];
  keyName?: "tags" | "tagList";
}) {
  const featureFlags = useFeatureFlag();
  const ignoreCase = !!featureFlags.CUSTOM_ATTR_IGNORE_CASE;

  const customColumns: ACTableColumnType<T>[] = [];
  if (!isUndefined(externalColumn)) {
    externalColumn.forEach((col) => {
      customColumns.push({
        title: col.columnName,
        dataIndex: `keyValue.${col.key}`,
        key: `keyValue.${col.key}`,
        render: (_value, record) => {
          const values = resolveCustomColumnValues(
            record,
            col.key,
            keyName,
            ignoreCase,
          );
          return (
            <ACResponsiveTags tags={values} hidePopoverOnSingleItem={true} />
          );
        },
        width: 125,
      });
    });
  }
  return customColumns;
}

/**
 * Returns a string for a custom column cell.
 * dataIndex format: "keyValue.<columnKey>"
 */
export function getResolvedCustomColumnValue<
  T extends { tags?: string[] | string; tagList?: string[] | string },
>(
  record: T,
  dataIndex: string,
  keyName: "tags" | "tagList" = "tags",
  ignoreCase = false,
): string {
  const parts = dataIndex?.split?.(".");
  const customKey = parts?.slice(1).join(".");
  const values = resolveCustomColumnValues(
    record,
    customKey,
    keyName,
    ignoreCase,
  );
  return isEmpty(values) ? CellRenderers.VALUE_OR_NA() : values.join(", ");
}
